import type { Ref, SVGProps } from 'react'

import { cn } from '@gto/ui/cn'

import { circularProgressVariants } from './circular-progress.styles'

type CircularProgressColor = 'primary' | 'secondary' | 'inherit' | 'error'

/**
 * Determinate fills an arc to a fixed percentage; indeterminate spins an
 * unbounded wait. Indeterminate is the default — the common loading-spinner case.
 */
type CircularProgressType = 'determinate' | 'indeterminate'

/** MUI's fixed 44-unit coordinate system; `size` scales the rendered svg only. */
const VIEWBOX_SIZE = 44
const DEFAULT_SIZE = 40
const DEFAULT_THICKNESS = 3.6

type CircularProgressBaseProps = Omit<
  SVGProps<SVGSVGElement>,
  // The component owns its accessibility semantics — never let a spread-through
  // prop clobber them (mirrors the LinearProgress sibling's `role` omission).
  'color' | 'ref' | 'role' | 'aria-valuenow' | 'aria-valuemin' | 'aria-valuemax'
> & {
  /** Arc colour from the design system. Defaults to `primary`. */
  color?: CircularProgressColor
  /** Rendered diameter in px. Defaults to 40. */
  size?: number
  /** Stroke width in the 44-unit viewBox. Defaults to 3.6. */
  thickness?: number
  /** Accessible label rendered as the svg's `<title>` (native hover tooltip). */
  title?: string
  className?: string
  ref?: Ref<SVGSVGElement>
  'data-tst'?: string
}

/**
 * `type` discriminates the two shapes:
 * - `indeterminate` (the default) spins continuously and has no progress value.
 * - `determinate` requires `value` and may tune the arc cap / transition.
 */
type CircularProgressProps =
  | (CircularProgressBaseProps & {
      type?: 'indeterminate'
    })
  | (CircularProgressBaseProps & {
      type: 'determinate'
      /** Progress percentage 0–100. Clamped to that range before rendering. */
      value: number
      /** Arc end-cap. Defaults to `butt` (square), matching MUI. */
      strokeLinecap?: 'butt' | 'round'
      /**
       * Drop the smooth fill animation. Set for high-frequency updaters (e.g. a
       * per-frame countdown) where the 300ms transition would lag behind.
       */
      disableTransition?: boolean
    })

const clampPercent = (value: number): number => Math.min(100, Math.max(0, value))

const CircularProgress = (props: CircularProgressProps) => {
  const isDeterminate = props.type === 'determinate'

  // Normalise the union so every component-owned key is present, then
  // destructure all of them out — `domProps` is left with only SVG attributes.
  const {
    color = 'primary',
    // Pulled out of the union so it never leaks onto the svg via `domProps`.
    type: _type,
    size = DEFAULT_SIZE,
    thickness = DEFAULT_THICKNESS,
    value,
    strokeLinecap = 'butt',
    disableTransition = false,
    title,
    className,
    ref,
    'data-tst': dataTestId,
    ...domProps
  } = isDeterminate
    ? props
    : { ...props, value: undefined, strokeLinecap: undefined, disableTransition: undefined }

  const clampedValue = isDeterminate && value != null ? clampPercent(value) : undefined

  const radius = (VIEWBOX_SIZE - thickness) / 2
  const circumference = 2 * Math.PI * radius
  const dashOffset = clampedValue == null ? undefined : (circumference * (100 - clampedValue)) / 100

  const styles = circularProgressVariants({
    color,
    type: isDeterminate ? 'determinate' : 'indeterminate',
  })

  return (
    <svg
      ref={ref}
      aria-valuemax={isDeterminate ? 100 : undefined}
      aria-valuemin={isDeterminate ? 0 : undefined}
      aria-valuenow={clampedValue}
      className={cn(styles.root(), className)}
      data-tst={dataTestId}
      data-ui="circular-progress"
      height={size}
      role="progressbar"
      viewBox={`${VIEWBOX_SIZE / 2} ${VIEWBOX_SIZE / 2} ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
      width={size}
      {...domProps}
    >
      {title != null && <title>{title}</title>}
      <circle
        cx={VIEWBOX_SIZE}
        cy={VIEWBOX_SIZE}
        r={radius}
        strokeDasharray={isDeterminate ? circumference : '80px, 200px'}
        strokeDashoffset={dashOffset}
        strokeLinecap={strokeLinecap}
        strokeWidth={thickness}
        className={cn(
          styles.circle(),
          isDeterminate &&
            !disableTransition &&
            'transition-[stroke-dashoffset] duration-300 ease-in-out',
        )}
      />
    </svg>
  )
}

export { CircularProgress }
export type { CircularProgressColor, CircularProgressProps, CircularProgressType }
